/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { RefObject } from 'preact';
import { useRef, useState } from 'preact/hooks';
import { getRandomId } from '#utils/PRIVATE_visLayoutUtils/layoutUtils';
import { getInfo, cancelEvent } from '#utils/UNSAFE_visUtils';
import { ChartGroupInfo, Info } from '#utils/UNSAFE_visTypes/chart';
import { useVisHover } from '#hooks/PRIVATE_useVisHover';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { AxisInfo } from '#PRIVATE_Axis/axis.types';

type Props = {
  rootRef: RefObject<HTMLElement>;
  getNextChartItem: (
    info: Info,
    arrow: 'ArrowLeft' | 'ArrowRight' | 'ArrowDown' | 'ArrowUp'
  ) => Info;
  getNextRefObject: (
    info: Info,
    arrow: 'ArrowLeft' | 'ArrowRight' | 'ArrowDown' | 'ArrowUp'
  ) => Info;
  getNextNavGroup: (
    info: ChartGroupInfo,
    arrow: 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown'
  ) => ChartGroupInfo;
  getDefaultNavGroup: () => ChartGroupInfo;
  xAxisInfo: AxisInfo;
  touchResponse: 'touchStart' | 'touchHold';
  onItemHover?: (detail: { seriesIndex?: number; groupIndex?: number }) => void;
  onItemFocus?: (detail: { seriesIndex?: number; groupIndex?: number }) => void;
  drilling?: 'on' | 'groupsOnly' | 'off';
};

/**
 * Returns whether the two groups are same.
 * @param g1 GroupInfo
 * @param g2 GroupInfo
 * @returns
 */
function isGroupEqual(g1?: Partial<ChartGroupInfo>, g2?: Partial<ChartGroupInfo>) {
  return (
    g1?.levelIndex === g2?.levelIndex &&
    g1?.groupIndex === g2?.groupIndex &&
    g1?.isCurrent === g2?.isCurrent &&
    g1?.isFocusVisible === g2?.isFocusVisible
  );
}

/**
 * Returns whether the two item info are same.
 * @param i1 ItemInfo
 * @param i2 ItemInfo
 * @returns
 */
function isItemEqual(i1?: Info, i2?: Info) {
  return (
    i1?.seriesIndex === i2?.seriesIndex && isGroupEqual(i1, i2) && i1?.refObjId === i2?.refObjId
  );
}

/**
 * Returns whether the two reference info are same.
 * @param i1 ItemInfo
 * @param i2 ItemInfo
 * @returns
 */
function isrefObjIdEqual(i1?: Info, i2?: Info) {
  return i1?.refObjId === i2?.refObjId;
}

/**
 * Returns whether the two item info are same.
 * @param i1 ItemInfo
 * @param i2 ItemInfo
 * @returns
 */
function isItemInfoEqual(i1?: Info, i2?: Info) {
  return i1?.seriesIndex === i2?.seriesIndex && i2?.groupIndex === i1?.groupIndex;
}

/**
 * Hook to handle chart navigation.
 */
export function useChartNav({
  rootRef,
  getNextChartItem,
  getNextRefObject,
  getDefaultNavGroup,
  getNextNavGroup,
  xAxisInfo,
  touchResponse,
  onItemHover,
  onItemFocus,
  drilling
}: Props) {
  const [focusedItemInfo, setfocusedItemInfo] = useState<Info>({
    groupIndex: xAxisInfo.startIndex || 0,
    seriesIndex: 0
  });

  const isGroupDrillEnabled = drilling === 'on' || drilling === 'groupsOnly';
  const [hoveredItemInfo, setHoveredItemInfo] = useState<Info>();

  const [focusedGroupInfo, setFocusedGroupInfo] = useState<ChartGroupInfo>(getDefaultNavGroup());

  const activeId = useRef<string>();
  const previousHoveredItemRef = useRef<Info>();
  const previousFocusedItemRef = useRef<Info>();

  const isGroupFocused = focusedGroupInfo?.isCurrent;
  const isRefFocused = !!focusedItemInfo.isCurrent && focusedItemInfo.refObjId != undefined;
  const isDataItemFocused =
    focusedItemInfo.refObjId == undefined &&
    !!focusedItemInfo.isCurrent &&
    focusedItemInfo.seriesIndex != undefined;

  const updateGroupInfo = (groupInfo: ChartGroupInfo) => {
    if (!isGroupEqual(groupInfo, focusedGroupInfo)) {
      setFocusedGroupInfo(groupInfo);
      activeId.current = getRandomId();
    }
  };

  const update = (type: 'hover' | 'focus', info?: Info) => {
    const prevInfoRef = type === 'hover' ? previousHoveredItemRef : previousFocusedItemRef;
    const callback = type === 'hover' ? onItemHover : onItemFocus;
    if (!isItemEqual(info, prevInfoRef.current)) {
      const input = { groupIndex: info?.groupIndex, seriesIndex: info?.seriesIndex };
      callback?.(input);
      prevInfoRef.current = info;
      activeId.current = getRandomId();
    }
  };

  const updateFocusedItemInfo = (itemInfo: Info) => {
    if (!isItemEqual(itemInfo, focusedItemInfo)) {
      setfocusedItemInfo(itemInfo);
      // preserve hovered effect if same item is focused
      if (hoveredItemInfo && !isItemInfoEqual(hoveredItemInfo, itemInfo)) {
        setHoveredItemInfo({ ...hoveredItemInfo, isCurrent: false });
      }
      if (itemInfo.isCurrent && itemInfo.seriesIndex !== undefined) {
        update('focus', itemInfo);
      }
    }
  };

  const updateFocusedRefInfo = (refInfo: Info) => {
    if (!isItemEqual(refInfo, focusedItemInfo)) {
      setfocusedItemInfo(refInfo);
      setHoveredItemInfo(undefined);
      activeId.current = getRandomId();
    }
  };

  const updateHoveredItemInfo = (itemInfo?: Info) => {
    if (
      !hoveredItemInfo ||
      !isItemEqual(itemInfo, hoveredItemInfo) ||
      !isrefObjIdEqual(itemInfo, hoveredItemInfo)
    ) {
      setHoveredItemInfo(itemInfo);
      setfocusedItemInfo({ ...focusedItemInfo, isCurrent: false });
      update('hover', itemInfo);
    }
  };

  const handleTab = (event: KeyboardEvent) => {
    const defaultGroup = getDefaultNavGroup();
    const validItemInfoIndex = Math.max(
      xAxisInfo.startIndex!,
      Math.min(xAxisInfo.endIndex!, focusedItemInfo.groupIndex!)
    );

    const getDefaultRef = (dir: 'forward' | 'backward') =>
      getNextRefObject({}, dir === 'forward' ? 'ArrowRight' : 'ArrowLeft');

    if (event.shiftKey) {
      // Backward cycle: group -> refObjId -> item -> tabbout
      if (isGroupFocused) {
        const prevRef = getDefaultRef('backward');
        if (prevRef.refObjId != undefined) {
          updateFocusedRefInfo({
            ...focusedItemInfo,
            refObjId: prevRef.refObjId,
            isCurrent: true,
            isFocusVisible: true
          });
          updateGroupInfo({
            ...focusedGroupInfo,
            ...defaultGroup,
            isCurrent: false,
            isFocusVisible: false
          });
          cancelEvent(event);
        } else {
          // No ref objects: go directly to item
          updateFocusedItemInfo({
            ...focusedItemInfo,
            groupIndex: validItemInfoIndex,
            seriesIndex: focusedItemInfo.seriesIndex ?? 0,
            refObjId: undefined,
            isCurrent: true,
            isFocusVisible: true
          } as Info);
          updateGroupInfo({
            ...focusedGroupInfo,
            ...defaultGroup,
            isCurrent: false,
            isFocusVisible: false
          });
          cancelEvent(event);
        }
      } else if (isRefFocused) {
        // Move to item
        updateFocusedItemInfo({
          ...focusedItemInfo,
          groupIndex: validItemInfoIndex,
          seriesIndex: focusedItemInfo.seriesIndex ?? 0,
          refObjId: undefined,
          isCurrent: true,
          isFocusVisible: true
        } as Info);
        cancelEvent(event);
      } else if (isDataItemFocused) {
        // Backward tabbout from item: let default browser behavior move focus out
        update('focus', undefined);
        // do not cancel event
      } else if (isGroupDrillEnabled) {
        // If nothing is focused yet, focus group on Shift+Tab within component
        updateGroupInfo({
          ...focusedGroupInfo,
          ...defaultGroup,
          isCurrent: true,
          isFocusVisible: true
        });
        cancelEvent(event);
      }
    } else {
      // Forward cycle: item -> refObjId -> group -> tabbout
      if (isDataItemFocused) {
        const nextRef = getDefaultRef('forward');
        if (nextRef.refObjId != undefined) {
          updateFocusedRefInfo({
            ...focusedItemInfo,
            refObjId: nextRef.refObjId,
            isCurrent: true,
            isFocusVisible: true
          });
          update('focus', undefined);
          cancelEvent(event);
          activeId.current = getRandomId();
          return;
        }
        if (isGroupDrillEnabled) {
          updateGroupInfo({
            ...focusedGroupInfo,
            ...defaultGroup,
            isCurrent: true,
            isFocusVisible: true
          });
          // keep item indices tracked but not current
          updateFocusedItemInfo({
            ...focusedItemInfo,
            isCurrent: false,
            isFocusVisible: false
          });
          cancelEvent(event);
          return;
        }
        // No ref objects and no group navigation: tabbout
        update('focus', undefined);
        // allow default tabbout
      } else if (isRefFocused) {
        if (isGroupDrillEnabled) {
          updateGroupInfo({
            ...focusedGroupInfo,
            ...defaultGroup,
            isCurrent: true,
            isFocusVisible: true
          });
          // keep item indices tracked but not current
          updateFocusedItemInfo({
            ...focusedItemInfo,
            isCurrent: false,
            isFocusVisible: false
          });
          cancelEvent(event);
        }
        // else: tabbout (do not cancel)
      } else if (isGroupFocused) {
        // Forward tabbout from group: let browser move focus out
        updateGroupInfo({
          ...focusedGroupInfo,
          ...defaultGroup,
          isCurrent: false,
          isFocusVisible: false
        });
        // do not cancel
      } else {
        // Initialize focus to item if nothing is focused yet
        updateFocusedItemInfo({
          ...focusedItemInfo,
          groupIndex: validItemInfoIndex,
          isCurrent: true,
          isFocusVisible: true
        });
        cancelEvent(event);
      }
    }
    activeId.current = getRandomId();
  };

  const onKeyUp = (event: KeyboardEvent) => {
    const key = event.key;
    switch (key) {
      case 'Tab': {
        if (!isDataItemFocused && !isGroupFocused && !isRefFocused) {
          handleTab(event);
        }
        break;
      }
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const key = event.key;
    switch (key) {
      case 'Tab': {
        handleTab(event);
        break;
      }
      case 'ArrowRight': {
        if (isGroupFocused) {
          const info = getNextNavGroup(focusedGroupInfo, key);
          updateGroupInfo({
            ...info,
            isCurrent: true,
            isFocusVisible: true
          });
        } else if (isRefFocused) {
          const info = getNextRefObject(focusedItemInfo, key);
          updateFocusedRefInfo({
            ...focusedItemInfo,
            ...info,
            isCurrent: true,
            isFocusVisible: true
          });
        } else {
          const info = getNextChartItem(focusedItemInfo, key);
          updateFocusedItemInfo({
            ...info,
            isCurrent: true,
            isFocusVisible: true
          });
        }
        setHoveredItemInfo(undefined);
        cancelEvent(event);
        break;
      }
      case 'ArrowLeft': {
        if (isGroupFocused) {
          const info = getNextNavGroup(focusedGroupInfo, key);
          updateGroupInfo({
            ...info,
            isCurrent: true,
            isFocusVisible: true
          });
        } else if (isRefFocused) {
          const info = getNextRefObject(focusedItemInfo, key);
          updateFocusedRefInfo({
            ...focusedItemInfo,
            ...info,
            isCurrent: true,
            isFocusVisible: true
          });
        } else {
          const info = getNextChartItem(focusedItemInfo, key);
          updateFocusedItemInfo({
            ...info,
            isCurrent: true,
            isFocusVisible: true
          });
        }
        setHoveredItemInfo(undefined);
        cancelEvent(event);
        break;
      }
      case 'ArrowUp': {
        if (isDataItemFocused) {
          const info = getNextChartItem(focusedItemInfo, key);
          updateFocusedItemInfo({
            ...info,
            isCurrent: true,
            isFocusVisible: true
          });
        } else if (isGroupFocused) {
          const info = getNextNavGroup(focusedGroupInfo, 'ArrowUp');
          updateGroupInfo({
            ...info,
            isCurrent: true,
            isFocusVisible: true
          });
        } else if (isRefFocused) {
          const info = getNextRefObject(focusedItemInfo, key);
          updateFocusedRefInfo({
            ...focusedItemInfo,
            ...info,
            isCurrent: true,
            isFocusVisible: true
          });
        }
        setHoveredItemInfo(undefined);
        cancelEvent(event);
        break;
      }
      case 'ArrowDown': {
        if (isDataItemFocused) {
          const info = getNextChartItem(focusedItemInfo, key);
          updateFocusedItemInfo({
            ...info,
            isCurrent: true,
            isFocusVisible: true
          });
        } else if (isGroupFocused) {
          const info = getNextNavGroup(focusedGroupInfo, 'ArrowDown');
          updateGroupInfo({
            ...info,
            isCurrent: true,
            isFocusVisible: true
          });
        } else if (isRefFocused) {
          const info = getNextRefObject(focusedItemInfo, key);
          updateFocusedRefInfo({
            ...focusedItemInfo,
            ...info,
            isCurrent: true,
            isFocusVisible: true
          });
        }
        setHoveredItemInfo(undefined);
        cancelEvent(event);
        break;
      }
    }
  };

  const onPointerUp = (event: PointerEvent) => {
    const info = getInfo(rootRef, event.target as HTMLElement);

    if (!info) {
      return;
    }
    const { seriesIndex, groupIndex, levelIndex, refObjId } = info;

    if (seriesIndex != undefined && groupIndex != undefined) {
      updateFocusedItemInfo({
        seriesIndex,
        groupIndex,
        isCurrent: false,
        isFocusVisible: false
      });
      updateGroupInfo({
        ...focusedGroupInfo,
        isCurrent: false,
        isFocusVisible: false
      });
    } else if (refObjId != undefined) {
      updateFocusedRefInfo({
        ...focusedItemInfo,
        refObjId,
        isCurrent: false,
        isFocusVisible: false
      });
      updateGroupInfo({
        ...focusedGroupInfo,
        isCurrent: false,
        isFocusVisible: false
      });
    } else if (groupIndex !== undefined && seriesIndex === undefined && levelIndex != undefined) {
      updateGroupInfo({
        levelIndex,
        groupIndex,
        isCurrent: true,
        isFocusVisible: false
      });
      updateFocusedItemInfo({
        ...focusedItemInfo,
        isCurrent: false,
        isFocusVisible: false
      });
    }
  };

  const onHover = (event: PointerEvent) => {
    const info = getInfo(rootRef, event.target as HTMLElement);
    if (!info) {
      updateHoveredItemInfo(undefined);
      return;
    }
    const { seriesIndex, groupIndex, refObjId } = info;

    if (seriesIndex != undefined) {
      updateHoveredItemInfo({
        seriesIndex,
        groupIndex,
        isCurrent: true
      });
      updateFocusedItemInfo({
        ...focusedItemInfo,
        isCurrent: false
      });
    } else if (refObjId != undefined) {
      updateHoveredItemInfo({
        refObjId,
        isCurrent: true
      });
      updateFocusedItemInfo({
        ...focusedItemInfo,
        isCurrent: false
      });
    }
  };

  const onHoverLeave = () => {
    updateHoveredItemInfo(undefined);
  };

  const hoverHandlers = useVisHover(onHover, undefined, onHoverLeave, touchResponse);

  const onFocusUpdate = () => {
    const validItemInfoIndex = Math.max(
      xAxisInfo.startIndex!,
      Math.min(xAxisInfo.endIndex!, focusedItemInfo.groupIndex!)
    );
    updateFocusedItemInfo({
      ...focusedItemInfo,
      groupIndex: validItemInfoIndex,
      isCurrent: true,
      isFocusVisible: true
    });
  };

  const onBlur = () => {
    updateFocusedItemInfo({
      ...focusedItemInfo,
      isCurrent: false,
      isFocusVisible: false
    });
  };

  return {
    focusedItemInfo,
    focusedGroupInfo,
    hoveredItemInfo,
    activeId: activeId.current,
    navProps: mergeProps(hoverHandlers, {
      onKeyDown,
      onKeyUp,
      onPointerUp,
      onBlur
    }),
    onFocusUpdate
  };
}
